/**
 * Done. CPD — Public Header (Stitch v2)
 *
 * Sticky/fixed top app bar shown on the public LandingPage. Smooth-scroll
 * anchors on desktop; full-height slide-in drawer on mobile (≤ md).
 * Auth-side surfaces continue to use src/components/features/TopNav.tsx.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';

type SectionId = 'hero' | 'about' | 'how' | 'start' | 'learn';

interface NavItem {
  id: SectionId;
  label: string;
  icon: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'hero',  label: 'Home',         icon: 'home' },
  { id: 'about', label: 'About',        icon: 'info' },
  { id: 'how',   label: 'How It Works', icon: 'account_tree' },
  { id: 'learn', label: 'Learn',        icon: 'library_books' },
];

function scrollToSection(id: SectionId): void {
  document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function PublicHeader({ onStart }: { onStart: () => void }): React.ReactElement {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [, navigate] = useLocation();

  const closeDrawer = useCallback(() => { setDrawerOpen(false); }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') closeDrawer(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [drawerOpen, closeDrawer]);

  const handleNav = (id: SectionId): void => {
    closeDrawer();
    scrollToSection(id);
  };

  const handleStart = (): void => {
    closeDrawer();
    onStart();
  };

  const goLogin = (): void => {
    closeDrawer();
    navigate('/login');
  };

  return (
    <>
      <header
        className="fixed top-0 inset-x-0 z-50 h-20 bg-[var(--stitch-surface)]/85 backdrop-blur-md border-b border-[var(--stitch-surface-container-highest)]"
        data-print-hidden
      >
        <div
          className="h-full mx-auto flex items-center justify-between px-4 md:px-10"
          style={{ maxWidth: 'var(--stitch-container-max)' }}
        >
          {/* Brand */}
          <Link
            href="/"
            className="flex items-center gap-2 active:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--stitch-primary)] rounded"
          >
            <span className="material-symbols-outlined text-[28px] text-[var(--stitch-primary)]">clinical_notes</span>
            <span className="stitch-font-display text-[24px] md:text-[28px] font-bold tracking-tighter text-[var(--stitch-primary)] uppercase">
              Done. CPD
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
            {NAV_ITEMS.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => { handleNav(item.id); }}
                className="stitch-font-label text-[14px] text-[var(--stitch-on-surface-variant)] hover:text-[var(--stitch-primary)] transition-colors uppercase"
              >
                {item.label}
              </button>
            ))}
            <button
              type="button"
              onClick={handleStart}
              className="px-6 py-2 bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] rounded-full stitch-font-label text-[14px] uppercase hover:bg-[var(--stitch-primary-container)] transition-colors"
            >
              Start
            </button>
            <button
              type="button"
              onClick={goLogin}
              className="stitch-font-label text-[14px] uppercase text-[var(--stitch-primary)] hover:text-[var(--stitch-primary-container)] transition-colors"
            >
              Login
            </button>
          </nav>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="md:hidden p-2 text-[var(--stitch-primary)] min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-expanded={drawerOpen}
            aria-controls="public-mobile-drawer"
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            onClick={() => { setDrawerOpen(o => !o); }}
          >
            <span className="material-symbols-outlined text-[28px]">{drawerOpen ? 'close' : 'menu'}</span>
          </button>
        </div>
      </header>

      {/* Mobile drawer + overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-[60] transition-opacity ${drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />
      <aside
        id="public-mobile-drawer"
        className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-[var(--stitch-surface)] shadow-2xl z-[70] transform transition-transform duration-300 ease-in-out ${drawerOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 flex justify-between items-center border-b border-[var(--stitch-outline-variant)]">
            <span className="stitch-font-display text-[24px] font-bold text-[var(--stitch-primary)] uppercase">Done. CPD</span>
            <button
              type="button"
              onClick={closeDrawer}
              className="text-[var(--stitch-on-surface-variant)] p-2"
              aria-label="Close menu"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <nav className="flex flex-col gap-1 p-4" aria-label="Mobile">
            {NAV_ITEMS.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => { handleNav(item.id); }}
                className="flex items-center gap-4 px-4 py-3 text-left stitch-font-label uppercase text-[var(--stitch-on-surface-variant)] hover:bg-[var(--stitch-surface-container)] rounded-xl transition-colors"
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                {item.label}
              </button>
            ))}
            <button
              type="button"
              onClick={handleStart}
              className="flex items-center gap-4 px-4 py-3 mt-2 text-left stitch-font-label uppercase bg-[var(--stitch-primary-container)] text-[var(--stitch-on-primary-container)] rounded-xl"
            >
              <span className="material-symbols-outlined">play_arrow</span>
              Start
            </button>
            <button
              type="button"
              onClick={goLogin}
              className="flex items-center gap-4 px-4 py-3 text-left stitch-font-label uppercase text-[var(--stitch-primary)] hover:bg-[var(--stitch-surface-container)] rounded-xl transition-colors"
            >
              <span className="material-symbols-outlined">login</span>
              Login
            </button>
          </nav>
        </div>
      </aside>
    </>
  );
}
